"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TabList = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _customPropTypes = _interopRequireDefault(require("../../util/custom-prop-types"));
var _Tabs = require("../Tabs/Tabs.context");
var _tabs = require("../Tabs/tabs.focusTab");
var _TabList = require("./TabList.styles");
var _excluded = ["appearance", "children", "defaultSelected", "fluid", "manualActivation", "onSelected", "selected"];
/**
 * A `Tabs.List` is a collection of `Tabs.Tab` components. Similiar to `Radio` controls,
 * the tabs in a `Tabs.List` are **mutually exclusive** -- only one `Tabs.Tab` can be
 * selected at any given time.
 *
 * @extends ToggleButtonGroup
 * @since 8.10.0
 */
var TabList = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    children = _ref.children,
    defaultSelected = _ref.defaultSelected,
    fluid = _ref.fluid,
    manualActivation = _ref.manualActivation,
    onSelected = _ref.onSelected,
    selected = _ref.selected,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var context = (0, _react.useContext)(_Tabs.TabsContext);
  var defaultInnerRef = (0, _react.useRef)();
  var innerRef = forwardedRef || defaultInnerRef;
  var onSelectedCallback = (0, _react.useCallback)(function (id) {
    // If uncontrolled and is a child of `Tabs`, update the `Tabs` internal state.
    if (typeof context.selectedState !== 'undefined') {
      context.setSelectedState(id);
    }
    if (context.onSelected) {
      context.onSelected(id);
    }
    if (onSelected) {
      onSelected(id);
    }
  }, [context, onSelected]);
  var onKeyDownCallback = (0, _react.useCallback)(function (e) {
    var selectedTab;
    if (e.keyCode === _constants.KEY_CODES.ARROW_RIGHT) {
      selectedTab = (0, _tabs.focusNextTab)(innerRef.current, false);
      e.preventDefault();
    } else if (e.keyCode === _constants.KEY_CODES.ARROW_LEFT) {
      selectedTab = (0, _tabs.focusPreviousTab)(innerRef.current, true);
      e.preventDefault();
    }
    if (!context.manualActivation && !manualActivation && typeof selectedTab !== 'undefined') {
      selectedTab.click();
    }
  }, [context.manualActivation, manualActivation, innerRef]);
  return /*#__PURE__*/_react.default.createElement(_TabList.StyledTabList, (0, _extends2.default)({
    appearance: context.appearance || appearance,
    defaultValue: context.selectedState || defaultSelected,
    fluid: typeof context.fluid !== 'undefined' ? context.fluid : fluid,
    onChange: onSelectedCallback,
    onKeyDown: onKeyDownCallback,
    ref: innerRef,
    role: "tablist",
    value: context.selected || selected
  }, rest), _react.Children.map(children, function (child) {
    return (
      // Provide auto-generated `aria-controls` if missing.
      // Add `value` for use by ToggleButtonGroup
      child && /*#__PURE__*/(0, _react.cloneElement)(child, {
        'aria-controls': typeof child.props['aria-controls'] !== 'undefined' ? child.props['aria-controls'] : child.props.id + "_panel",
        'value': child.props.id
      })
    );
  }));
});
exports.TabList = TabList;
TabList.displayName = 'Tabs.List';
TabList.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The style of `Tabs.Tab` to display. Can be overridden by
   * `appearance` set on the parent `Tabs` component.
   */
  'appearance': _propTypes.default.oneOf(Object.values(_constants.TABS_APPEARANCES)),
  /**
   * An [aria-label](https://www.w3.org/TR/wai-aria-1.2/#aria-label) is not required
   * but may be used to describe the `Tabs` instance.
   */
  'aria-label': _propTypes.default.string,
  /**
   * A group of `Tabs.Tab` components.
   */
  'children': _propTypes.default.node.isRequired,
  /**
   * The id of the `Tabs.Tab` to select by default.
   * When set, `Tabs.List` will behave as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * If `Tabs.List` is a child of `Tabs`, set `defaultSelected` on
   * `Tabs` instead. Otherwise, `Tabs.List` must be set with either
   * `selected` or `defaultSelected`, but not both.
   */
  'defaultSelected': _propTypes.default.string,
  /**
   * When `true`, the `Tabs.List` stretches to fill its container and tries to maintain equal
   * tab widths. Can be overridden by `fluid` set on the
   * parent `Tabs` component.
   */
  'fluid': _customPropTypes.default.inlineMediaQuery(_propTypes.default.bool),
  /**
   * Align tabs within `Tabs.List`
   */
  'justifyContent': _customPropTypes.default.inlineMediaQuery(_propTypes.default.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly'])),
  /**
   * Determines the behavior when navigating the `Tabs.List` with arrow keys. By default, when a
   * new `Tabs.Tab` is given focus, its corresponding `Tabs.Panel`
   * will display automatically. When `manualActivation` is set to "true",
   * the user must press the Spacebar or Enter key before the panel is displayed.
   *
   * In circumstances where content can be displayed instantly (i.e., all `Tabs.Panel` content is
   * present in the DOM), use the default behavior. If changing panels requires first loading the
   * content, set `manualActivation` to "true".
   * https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus
   */
  'manualActivation': _propTypes.default.bool,
  /**
   * Callback for when a new tab is selected. Receives the `id` of the selected
   * `Tabs.Tab`.
   */
  'onSelected': _propTypes.default.func,
  /**
   * The id of the selected `Tabs.Tab`. When set,
   * `Tabs.List` will behave as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * If `Tabs.List` is a child of `Tabs`, set `selected` on
   * `Tabs` instead.
   * Otherwise, `Tabs.List` must be set with either `selected` or `defaultSelected`, but not both.
   */
  'selected': _propTypes.default.string
} : {};
TabList.defaultProps = {
  appearance: _constants.TABS_APPEARANCES.default
};